import ReactDOMServer from "react-dom/server";
import L, { marker } from "leaflet";
import "leaflet-easybutton";
import "leaflet/dist/leaflet.css";
import "leaflet-easybutton/src/easy-button.css";
import cloneDeep from "lodash/cloneDeep";

import {
  House,
  Lock,
  LockOpen,
  Truck,
  Crosshair,
  Storefront,
} from "@phosphor-icons/react";

const toggleZoomAndDrag = (on, map = map) => {
  const properties = [
    window.map.dragging,
    window.map.touchZoom,
    window.map.doubleClickZoom,
    window.map.scrollWheelZoom,
    window.map.boxZoom,
    window.map.keyboard,
  ];

  for (const property of properties) {
    on ? property.enable() : property.disable();
  }
};

export const initDelivery = (map) => {
  if (!map) {
    console.error("Map object is not initialized");
    return;
  }

  console.log("Map initialized: ", map); // Debug log to check map initialization

  window.map = map;
  window.courierPolylineRoute = L.polyline([], {
    color: "red",
    weight: 2,
    opacity: 0.5,
    dashArray: "5",
    dashOffset: "10",
  }).addTo(window.map);

  const lockButtonIcon = ReactDOMServer.renderToStaticMarkup(
    <Lock size={24} weight="bold" />,
  );
  L.easyButton(
    `<span class="lock">${lockButtonIcon}</span>`,
    unlockHandler,
  ).addTo(window.map);
  if (window.initialInfo.destination.lat) {
    const homeButtonIcon = ReactDOMServer.renderToStaticMarkup(
      <House size={24} weight="bold" />,
    );
    L.easyButton(`<span>${homeButtonIcon}</span>`, homeHandler).addTo(
      window.map,
    );
  }
  toggleZoomAndDrag(false, window.map);
  displayCoordinates(window.initialInfo.route);
  if (
    window.initialInfo.status == "in_transit" ||
    window.initialInfo.status == "delivered"
  ) {
    createOriginButton();
    createCourierButton();
    createCenterButton();
    centerHandler();
  }
};

export const resetData = () => {
  window.initialInfo.messages = [];
  let cloned_messages = cloneDeep(window.initialInfo.messages);
  window.initialInfo.setMessages(cloned_messages);
};

export const createOriginButton = () => {
  const storeFrontButtonIcon = ReactDOMServer.renderToStaticMarkup(
    <Storefront size={24} weight="bold" />,
  );
  L.easyButton(
    `<span id="origin-btn">${storeFrontButtonIcon}</span>`,
    originHandler,
  ).addTo(window.map);
};

export const createCourierButton = () => {
  const truckButtonIcon = ReactDOMServer.renderToStaticMarkup(
    <Truck size={28} />,
  );
  L.easyButton(
    `<span id="courier-btn">${truckButtonIcon}</span>`,
    courierHandler,
  ).addTo(window.map);
};

export const createCenterButton = () => {
  const locationButtonIcon = ReactDOMServer.renderToStaticMarkup(
    <Crosshair size={24} weight="bold" />,
  );
  L.easyButton(
    `<span id="center-btn">${locationButtonIcon}</span>`,
    centerHandler,
  ).addTo(window.map);
};

const centerHandler = () => {
  if (window.courierPathRaw.length > 0) {
    //if delivered show full route
    if (window.initialInfo.status === "delivered") {
      window.map.flyToBounds(window.courierPathRaw, {
        animate: true,
        duration: 1,
      });
      return;
    }
    //else show courier and destination
    let courier = window.courierPathRaw[window.courierPathRaw.length - 1];
    let destination = window.initialInfo.destination;

    let bounds = [
      [courier.lat, courier.lng],
      [destination.lat, destination.lng],
    ];
    window.map.flyToBounds(bounds, { animate: true, duration: 1 });
  }
};

const originHandler = () => {
  if (window.courierPathRaw.length > 0) {
    window.map.flyTo(window.courierPathRaw[0], 17, {
      animate: true,
      duration: 1,
    });
  }
};

const courierHandler = () => {
  if (window.courierPathRaw.length > 0) {
    window.map.flyTo(
      window.courierPathRaw[window.courierPathRaw.length - 1],
      17,
      {
        animate: true,
        duration: 1,
      },
    );
  }
};

const homeHandler = () => {
  window.map.flyTo(window.initialInfo.destination, 17, {
    animate: true,
    duration: 1,
  });
};

const unlockHandler = () => {
  // toggle locked state
  window.locked = !window.locked;
  // If the map is locked, move view to last courier location
  // disable dragging and change lock button icon
  if (window.locked) {
    const icon = document.getElementsByClassName("lock")[0];
    icon.innerHTML = `<span class="lock">${ReactDOMServer.renderToStaticMarkup(<Lock size={24} weight="bold" />)}</span>`;
    // If the delivery hasn't started yet, lock onto the destination
    if (
      window.initialInfo.status === "requested" ||
      window.initialInfo.status === "in_preparation" ||
      window.initialInfo.status === "pickup_available" ||
      window.initialInfo.status === "failed"
    ) {
      window.map.setView(window.initialInfo.destination, 17, {
        animate: true,
        duration: 1,
      });
    }
    // If delivery is over, show entire path
    else if (window.initialInfo.status === "delivered") {
      if (window.courierPathRaw.length > 0) {
        if (window.courierPathRaw.length > 1) {
          let courier = window.courierPathRaw[0];
          let destination = window.initialInfo.destination;

          let bounds = [
            [courier.lat, courier.lng],
            [destination.lat, destination.lng],
          ];
          window.map.flyToBounds(bounds, { animate: true, duration: 1 });
        } else {
          window.map.flyTo(window.courierPathRaw[0], {
            animate: true,
            duration: 1,
          });
        }
      } else {
        window.map.setView(window.initialInfo.destination, 17, {
          animate: true,
          duration: 1,
        });
      }
    }
    // Lock onto the courier if in transit
    else {
      if (window.courierPathRaw.length > 0) {
        centerHandler();
      }
    }
    toggleZoomAndDrag(false, window.map);
  }
  // If the map is locked, enable dragging and change button icon
  else {
    const icon = document.getElementsByClassName("lock")[0];
    icon.innerHTML = `<span class="lock">${ReactDOMServer.renderToStaticMarkup(<LockOpen size={24} weight="bold" />)}</span>`;
    toggleZoomAndDrag(true, window.map);
  }
};

export const addMessage = (data) => {
  window.initialInfo.messages.push(data);
  let cloned_messages = cloneDeep(window.initialInfo.messages);
  window.initialInfo.setMessages(cloned_messages);
};

export const setDeliveryTime = (data) => {
  window.initialInfo.setDeliveryTime(data);
};

export const setOrderFulfillmentDate = (data) => {
  window.initialInfo.setOrderFulfillmentDate(data);
};

export const setStatus = (data) => {
  window.initialInfo.status = data;
  window.initialInfo.setDeliveryStatus(data);
};

export const displayCoordinates = (data) => {
  if (!window.map) {
    console.log("Map is not initialized when trying to display coordinates.");
    return;
  }

  // Transform coordinates into Latitude Longitude objects
  const parsedCoords = data
    .map((coord) => L.latLng({ lat: coord.lat, lng: coord.lng }))
    .filter((coord) => coord != null);

  if (
    parsedCoords.length > 0 &&
    window.initialInfo.status !== "requested" &&
    window.initialInfo.status !== "in_preparation" &&
    window.initialInfo.status !== "pickup_available"
  ) {
    window.courierPathRaw.push(...parsedCoords);

    if (window.originMarker == null) {
      window.originMarker = L.marker(L.latLng(parsedCoords[0]), {
        icon: window.originIcon,
      }).addTo(window.map);
    }

    // Draw marker at the end of the path
    if (window.initialInfo.status === "in_transit") {
      if (window.courierMarker == null) {
        window.courierMarker = L.marker(
          L.latLng(parsedCoords[data.length - 1]),
          {
            icon: window.courierIcon,
          },
        ).addTo(window.map);
      } else {
        window.courierMarker.setLatLng(
          window.courierPathRaw[window.courierPathRaw.length - 1],
        );
      }
      if (window.courierPathRaw.length > 0) {
        window.courierPolylineRoute.setLatLngs(window.courierPathRaw);
      }
    } else if (window.initialInfo.status === "delivered") {
      if (window.courierMarker != null) {
        window.map.removeLayer(window.courierMarker);
        window.courierMarker = null;
      }

      if (window.deliveredMarker == null) {
        window.deliveredMarker = L.marker(
          L.latLng(parsedCoords[data.length - 1]),
          {
            icon: window.deliveredIcon,
          },
        ).addTo(window.map);

        //draw full route
        if (window.courierPathRaw.length > 0) {
          window.courierPolylineRoute.setLatLngs(window.courierPathRaw);
        }
      } else {
        window.deliveredMarker.setLatLng(
          window.courierPathRaw[window.courierPathRaw.length - 1],
        );
      }
    }
  }

  // If the delivery hasn't started, center on the destination
  if (
    window.initialInfo.status === "requested" ||
    window.initialInfo.status === "in_preparation" ||
    window.initialInfo.status === "pickup_available" ||
    window.initialInfo.status === "failed"
  ) {
    window.map.setView(window.initialInfo.destination, 17, {
      animate: true,
      duration: 1,
    });
  }
  // If the delivery is over, center on the whole path
  else if (window.initialInfo.status === "delivered") {
    if (window.courierPathRaw.length > 0) {
      if (window.courierPathRaw.length > 1) {
        window.map.flyToBounds(window.courierPathRaw, {
          animate: true,
          duration: 1,
        });
      } else {
        window.map.setView(window.initialInfo.destination, 17, {
          animate: true,
          duration: 1,
        });
      }
    }
  }
  // If window is locked then change view to last courier position
  else if (window.locked) {
    if (
      window.initialInfo.status === "in_transit" ||
      window.initialInfo.status === "delivered"
    )
      centerHandler();
    else
      window.map.setView(window.initialInfo.destination, 17, {
        animate: true,
        duration: 1,
      });
  }
};

export const cancelDelivery = () => {
  window.map.setView(window.initialInfo.destination, 17, {
    animate: true,
    duration: 1,
  });
  //clear route
  if (window.courierPolylineRoute != null) {
    window.courierPolylineRoute.setLatLngs([]);
  }
  //remove buttons
  document
    .getElementById("center-btn")
    ?.parentElement.parentElement.parentElement.remove();
  document
    .getElementById("courier-btn")
    ?.parentElement.parentElement.parentElement.remove();
};
